import React from "react";

const StaffPersonalInfo = ({ staff }) => {
  // Use actual staff data from props
  const actualData = {
    contactDetails: {
      email: staff?.email || "Not provided",
      phone: staff?.mobile || "Not provided", 
      website: staff?.website || "Not provided",
      linkedin: staff?.linkedin || "Not provided",
    },
    workPermissions: {
      passports: staff?.passports || [],
      workPermits: staff?.workPermits || [],
      contractStatus: staff?.contractStatus || "Not specified",
      // Prefer the actual linked club name (passed through by the StaffProfile
      // mapper as currentClub), then current_organization, else "Free Agent".
      currentClub:
        staff?.currentClub ||
        staff?.club_details?.name ||
        staff?.current_organization ||
        "Free Agent",
      contractExpiring: staff?.contractExpiryDate || "Not specified",
      willingToRelocate: staff?.willingnessToRelocate || "Not specified",
      preferredRegions: staff?.preferredRegions || [],
    },
    technicalSkills: {
      videoAnalysis: staff?.videoAnalysis || [],
      gpsPerformance: staff?.gpsPerformance || [],
      software: staff?.software || [],
      specializations: staff?.specializations || [],
    },
    licenses: (staff?.licenses || []).map((license, index) => ({
      name: typeof license === 'string' ? license : license.title || license.name || `License ${index + 1}`,
      issueDate: typeof license === 'object' ? (license.year || license.issueDate || "Not specified") : "Not specified",
      id: typeof license === 'object' ? (license.authority || license.id || `ID${index + 1}`) : `ID${index + 1}`,
    })),
    languages: (staff?.languages || []).map((lang, index) => ({
      name: typeof lang === 'string' ? lang : lang.name || lang.language || `Language ${index + 1}`,
      level: typeof lang === 'object' ? (lang.level || "Not specified") : "Not specified",
    })),
    education: {
      institution: staff?.education?.institution || "Not provided",
      year: staff?.education?.year || "Not provided",
    },
    trainings: staff?.trainings || [],
  };

  // Same "find the matching upload in the documents pool" pattern as the
  // resume lookup in StaffCareerTimeline.js:71-79. `actualData.licenses[i]`
  // here is already flattened to the bare title string (see mapStaffData in
  // pages/search/StaffProfile.js), which is exactly what the license modal
  // uploads its document under (doc_type "license", title = license type).
  const getLicenseDocument = (licenseName) => {
    if (!staff?.documents || !Array.isArray(staff.documents)) return null;
    return staff.documents.find(
      (doc) => doc.doc_type === "license" && doc.title === licenseName
    );
  };

  return (
    <div className="space-y-6 p-5 rounded-xl bg-[#F8F9FB]">
      {/* CONTACT DETAILS */}
      <div className="bg-white p-4 rounded-lg">
        <h2 className="text-[#222A35] text-base font-bold mb-4">
          CONTACT DETAILS
        </h2>
        {/* max-w-full + wrap: the single row (email · phone · website · socials)
            used to size to its content and push the page body into horizontal
            scroll at 390, clipping the phone number. Desktop has the room, so
            it still renders on one line. */}
        <div className="bg-[#F8F9FB] w-fit max-w-full rounded-lg p-4">
          <div className="flex items-center gap-6 flex-wrap gap-y-3">
            <div className="flex items-center gap-2">
              <svg
                width="16"
                height="14"
                viewBox="0 0 16 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14.6673 2.99984C14.6673 2.2665 14.0673 1.6665 13.334 1.6665H2.66732C1.93398 1.6665 1.33398 2.2665 1.33398 2.99984M14.6673 2.99984V10.9998C14.6673 11.7332 14.0673 12.3332 13.334 12.3332H2.66732C1.93398 12.3332 1.33398 11.7332 1.33398 10.9998V2.99984M14.6673 2.99984L8.00065 7.6665L1.33398 2.99984"
                  stroke="#2C2C2C"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>

              <span className="text-base text-[#1A212F] font-medium">
                {actualData.contactDetails.email}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14.6669 11.2802V13.2802C14.6677 13.4659 14.6297 13.6497 14.5553 13.8198C14.4809 13.9899 14.3718 14.1426 14.235 14.2681C14.0982 14.3937 13.9367 14.4892 13.7608 14.5487C13.5849 14.6082 13.3985 14.6303 13.2136 14.6136C11.1622 14.3907 9.19161 13.6897 7.46028 12.5669C5.8495 11.5433 4.48384 10.1777 3.46028 8.56689C2.3336 6.8277 1.63244 4.84756 1.41361 2.78689C1.39695 2.60254 1.41886 2.41673 1.47795 2.24131C1.53703 2.06589 1.63199 1.90469 1.75679 1.76797C1.88159 1.63126 2.03348 1.52203 2.20281 1.44724C2.37213 1.37245 2.55517 1.33374 2.74028 1.33356H4.74028C5.06382 1.33038 5.37748 1.44495 5.62279 1.65592C5.8681 1.86689 6.02833 2.15986 6.07361 2.48023C6.15803 3.12027 6.31458 3.74871 6.54028 4.35356C6.62998 4.59218 6.64939 4.8515 6.59622 5.10081C6.54305 5.35012 6.41952 5.57897 6.24028 5.76023L5.39361 6.60689C6.34265 8.27592 7.72458 9.65786 9.39361 10.6069L10.2403 9.76023C10.4215 9.58099 10.6504 9.45746 10.8997 9.40429C11.149 9.35112 11.4083 9.37053 11.6469 9.46023C12.2518 9.68593 12.8802 9.84248 13.5203 9.92689C13.8441 9.97258 14.1399 10.1357 14.3513 10.3852C14.5627 10.6348 14.6751 10.9533 14.6669 11.2802Z"
                  stroke="#2C2C2C"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>

              <span className="text-base text-[#1A212F] font-medium">
                {actualData.contactDetails.phone}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14.6673 8.00016C14.6673 11.6821 11.6825 14.6668 8.00065 14.6668M14.6673 8.00016C14.6673 4.31826 11.6825 1.3335 8.00065 1.3335M14.6673 8.00016H1.33398M8.00065 14.6668C4.31875 14.6668 1.33398 11.6821 1.33398 8.00016M8.00065 14.6668C9.66817 12.8413 10.6158 10.4721 10.6673 8.00016C10.6158 5.52819 9.66817 3.15906 8.00065 1.3335M8.00065 14.6668C6.33313 12.8413 5.38548 10.4721 5.33398 8.00016C5.38548 5.52819 6.33313 3.15906 8.00065 1.3335M1.33398 8.00016C1.33398 4.31826 4.31875 1.3335 8.00065 1.3335"
                  stroke="#2C2C2C"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>

              <span className="text-base text-[#1A212F] font-medium">
                {actualData.contactDetails.website}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <svg
                width="16"
                height="15"
                viewBox="0 0 16 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.6673 5.3335C11.7282 5.3335 12.7456 5.75492 13.4957 6.50507C14.2459 7.25521 14.6673 8.27263 14.6673 9.3335V14.0002H12.0007V9.3335C12.0007 8.97987 11.8602 8.64074 11.6101 8.39069C11.3601 8.14064 11.0209 8.00016 10.6673 8.00016C10.3137 8.00016 9.97456 8.14064 9.72451 8.39069C9.47446 8.64074 9.33398 8.97987 9.33398 9.3335V14.0002H6.66732V9.3335C6.66732 8.27263 7.08874 7.25521 7.83889 6.50507C8.58904 5.75492 9.60645 5.3335 10.6673 5.3335Z"
                  stroke="#2C2C2C"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M4.00065 6.00016H1.33398V14.0002H4.00065V6.00016Z"
                  stroke="#2C2C2C"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M2.66732 4.00016C3.4037 4.00016 4.00065 3.40321 4.00065 2.66683C4.00065 1.93045 3.4037 1.3335 2.66732 1.3335C1.93094 1.3335 1.33398 1.93045 1.33398 2.66683C1.33398 3.40321 1.93094 4.00016 2.66732 4.00016Z"
                  stroke="#2C2C2C"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>

              <span className="text-base text-[#1A212F] font-medium">
                {actualData.contactDetails.linkedin}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* WORK PERMISSIONS */}
      <div className="bg-white p-4 rounded-lg">
        <h2 className="text-[#222A35] text-base font-bold mb-4">
          WORK PERMISSIONS
        </h2>
        <div className="">
          <div className="flex items-center gap-6">
            {/* First Row */}
            <div className="flex items-center justify-between border-r border-[#BEBEBE] pr-6">
              <div>
                <p className="text-sm text-[#1A212FB2] font-medium mb-2">
                  Passports held
                </p>
                <div className="flex items-center gap-4">
                  {actualData.workPermissions.passports.length > 0 ? (
                    actualData.workPermissions.passports.map((passport, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="w-4 h-3 rounded-sm bg-gray-300"></div>
                        <span className="text-base text-[#1A212F] font-medium">
                          {passport}
                        </span>
                      </div>
                    ))
                  ) : (
                    <span className="text-base text-[#646B7E] font-medium">
                      Not specified
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between pl-6">
              <div>
                <p className="text-sm text-[#1A212FB2] font-medium mb-2">
                  Work permits
                </p>
                <div className="flex items-center gap-4">
                  {actualData.workPermissions.workPermits.length > 0 ? (
                    actualData.workPermissions.workPermits.map((permit, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="w-4 h-3 rounded-sm bg-gray-300"></div>
                        <span className="text-base text-[#1A212F] font-medium">
                          {permit}
                        </span>
                      </div>
                    ))
                  ) : (
                    <span className="text-base text-[#646B7E] font-medium">
                      Not specified
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-[#D6DBEA] mt-6 pt-6">
            <div className="flex items-center gap-6">
              <div className="border-r border-[#BEBEBE] pr-6">
                <p className="text-sm text-[#1A212FB2] font-medium mb-4">
                  Contract status
                </p>
                <p className="text-base text-[#1A212F] font-medium">
                  {actualData.workPermissions.contractStatus}
                </p>
              </div>
              <div className="border-r border-[#BEBEBE] pr-6">
                <p className="text-sm text-[#1A212FB2] font-medium mb-4">
                  Current Club
                </p>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-600 rounded-sm"></div>
                  <p className="text-base text-[#1A212F] font-medium">
                    {actualData.workPermissions.currentClub}
                  </p>
                </div>
              </div>
              <div className="border-r border-[#BEBEBE] pr-6">
                <p className="text-sm text-[#1A212FB2] font-medium mb-4">
                  Contract expiring on
                </p>
                <p className="text-base text-[#1A212F] font-medium">
                  {actualData.workPermissions.contractExpiring}
                </p>
              </div>
              <div className="border-r border-[#BEBEBE] pr-6">
                <p className="text-sm text-[#1A212FB2] font-medium mb-4">
                  Willing to relocate
                </p>
                <p className="text-base text-[#1A212F] font-medium">
                  {actualData.workPermissions.willingToRelocate}
                </p>
              </div>
              <div>
                <p className="text-sm text-[#1A212FB2] font-medium mb-4">
                  Preferred regions
                </p>
                <div className="flex items-center gap-4">
                  {actualData.workPermissions.preferredRegions.length > 0 ? (
                    actualData.workPermissions.preferredRegions.map((region, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="w-4 h-3 rounded-sm bg-gray-300"></div>
                        <span className="text-base text-[#1A212F] font-medium">
                          {region}
                        </span>
                      </div>
                    ))
                  ) : (
                    <span className="text-base text-[#646B7E] font-medium">
                      Not specified
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* TECHNICAL SKILLS & SPECIALIZATIONS */}
      <div className="bg-white p-4 rounded-lg">
        <h2 className="text-[#222A35] text-base font-bold mb-4">
          TECHNICAL SKILLS & SPECIALIZATIONS
        </h2>
        <div className="">
          <div className="space-y-4">
            <div className="flex items-center gap-4 pb-4">
              <span className="text-sm text-[#1A212FB2] font-medium min-w-[120px]">
                Video/Analysis:
              </span>
              <div className="flex flex-wrap gap-2">
                {actualData.technicalSkills.videoAnalysis.length > 0 ? (
                  actualData.technicalSkills.videoAnalysis.map((skill, index) => (
                    <span
                      key={index}
                      className="px-3 py-2 bg-[#D6DBEA80] rounded-full text-sm text-[#1A212F] font-medium"
                    >
                      {skill}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-[#646B7E] font-medium">
                    Not specified
                  </span>
                )}
              </div>
            </div>
            <div className="border-t border-[#D6DBEA] pt-4 flex items-center gap-4 pb-4">
              <span className="text-sm text-[#1A212FB2] font-medium min-w-[120px]">
                GPS/Performance:
              </span>
              <div className="flex flex-wrap gap-2">
                {actualData.technicalSkills.gpsPerformance.length > 0 ? (
                  actualData.technicalSkills.gpsPerformance.map((skill, index) => (
                    <span
                      key={index}
                      className="px-3 py-2 bg-[#D6DBEA80] rounded-full text-sm text-[#1A212F] font-medium"
                    >
                      {skill}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-[#646B7E] font-medium">
                    Not specified
                  </span>
                )}
              </div>
            </div>
            <div className="border-t border-[#D6DBEA] pt-4 flex items-center gap-4 pb-4">
              <span className="text-sm text-[#1A212FB2] font-medium min-w-[120px]">
                Software:
              </span>
              <div className="flex flex-wrap gap-2">
                {actualData.technicalSkills.software.length > 0 ? (
                  actualData.technicalSkills.software.map((skill, index) => (
                    <span
                      key={index}
                      className="px-3 py-2 bg-[#D6DBEA80] rounded-full text-sm text-[#1A212F] font-medium"
                    >
                      {skill}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-[#646B7E] font-medium">
                    Not specified
                  </span>
                )}
              </div>
            </div>
            <div className="border-t border-[#D6DBEA] pt-4 flex items-center gap-4 pb-4">
              <span className="text-sm text-[#1A212FB2] font-medium min-w-[120px]">
                Specializations:
              </span>
              <div className="flex flex-wrap gap-2">
                {actualData.technicalSkills.specializations.length > 0 ? (
                  actualData.technicalSkills.specializations.map(
                    (skill, index) => (
                      <span
                        key={index}
                        className="px-3 py-2 bg-[#D6DBEA80] rounded-full text-sm text-[#1A212F] font-medium"
                      >
                        {skill}
                      </span>
                    )
                  )
                ) : (
                  <span className="text-sm text-[#646B7E] font-medium">
                    Not specified
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* LICENSES & CERTIFICATIONS and EDUCATION/UNIVERSITY in one row */}
      <div className="grid grid-cols-2 gap-6 ">
        {/* LICENSES & CERTIFICATIONS */}
        <div className="bg-white p-4 rounded-lg">
          <h2 className="text-[#222A35] text-base font-bold mb-4">
            LICENSES & CERTIFICATIONS
          </h2>
          <div className="">
            <div className="space-y-4">
              {actualData.licenses.length > 0 ? (
                actualData.licenses.map((license, index) => {
                  const licenseDoc = getLicenseDocument(license.name);
                  return (
                  <div
                    key={index}
                    className={`flex items-center gap-4  ${
                      index !== 0 ? "border-t border-[#D6DBEA] pt-4" : ""
                    }`}
                  >
                    <span className="px-4 py-2 bg-gradient-to-r from-[rgba(126,139,255,0.2)] to-[rgba(34,131,255,0.2)] text-[#374DF5] rounded-full text-sm font-medium flex items-center gap-2">
                      {license.name}
                      {/* Downloads the license's own uploaded file. Renders
                          only when the documents pool has a match — QAV5-405:
                          this used to be a dead download glyph shown for
                          every license regardless of whether a file existed. */}
                      {licenseDoc && (
                        <button
                          type="button"
                          onClick={() => window.open(licenseDoc.url, "_blank")}
                          aria-label={`Download document for ${license.name}`}
                          className="flex items-center"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M13 9V11.6667C13 12.0203 12.8595 12.3594 12.6095 12.6095C12.3594 12.8595 12.0203 13 11.6667 13H2.33333C1.97971 13 1.64057 12.8595 1.39052 12.6095C1.14048 12.3594 1 12.0203 1 11.6667V9M3.66667 5.66667L7 9M7 9L10.3333 5.66667M7 9V1"
                              stroke="#006BF3"
                              stroke-width="1.2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                        </button>
                      )}
                    </span>
                    <span className="text-sm text-[#1A212FCC]">
                      Issue Date: {license.issueDate} | ID: {license.id}
                    </span>
                  </div>
                  );
                })
              ) : (
                <div className="p-4 text-center">
                  <span className="text-sm text-[#646B7E] font-medium">
                    No licenses listed
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* EDUCATION/UNIVERSITY */}
        <div className="h-auto bg-white p-4 rounded-lg">
          <h2 className="text-[#222A35] text-base font-bold mb-4">
            EDUCATION/UNIVERSITY
          </h2>
          <div className="p-4">
            <div className="space-y-2 border-b border-[#D6DBEA] pb-4">
              <p className="text-xl text-[#222A35] font-bold">
                {actualData.education.institution}
              </p>
              <p className="text-sm text-[#646B7E]">
                Year: {actualData.education.year}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* LANGUAGES and FURTHER TRAININGS/WORKSHOPS in one row */}
      <div className="grid grid-cols-2 gap-6">
        {/* LANGUAGES */}
        <div className="bg-white p-4 rounded-lg">
          <h2 className="text-[#222A35] text-base font-bold mb-4">LANGUAGES</h2>
          <div className="">
            <div className="flex gap-4 flex-wrap items-center">
              {actualData.languages.length > 0 ? (
                actualData.languages.map((language, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="text-sm text-[#1A212F] font-medium">
                      {language.name}
                    </span>
                    <span className="text-sm text-[#646B7E]">|</span>
                    <span className="text-sm text-[#646B7E]">
                      {language.level}
                    </span>
                  </div>
                ))
              ) : (
                <span className="text-sm text-[#646B7E] font-medium">
                  Not specified
                </span>
              )}
            </div>
          </div>
        </div>

        {/* FURTHER TRAININGS/WORKSHOPS */}
        <div className="bg-white p-4 rounded-lg">
          <h2 className="text-[#222A35] text-base font-bold mb-4">
            FURTHER TRAININGS/WORKSHOPS
          </h2>
          <div className="">
            <div className="flex items-center gap-4">
              {actualData.trainings.length > 0 ? (
                <>
                  <span className="text-sm text-[#1A212F] font-medium">
                    {actualData.trainings[0].name}
                  </span>
                  <div className="bg-[#F8F9FB] p-2 rounded-lg">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13 9V11.6667C13 12.0203 12.8595 12.3594 12.6095 12.6095C12.3594 12.8595 12.0203 13 11.6667 13H2.33333C1.97971 13 1.64057 12.8595 1.39052 12.6095C1.14048 12.3594 1 12.0203 1 11.6667V9M3.66667 5.66667L7 9M7 9L10.3333 5.66667M7 9V1"
                        stroke="#006BF3"
                        stroke-width="1.2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                </>
              ) : (
                <span className="text-sm text-[#646B7E] font-medium">
                  Not specified
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffPersonalInfo;
